// useAstriaIndiaV2Tab.ts
import { useState } from "react";
import i18n from "../../../i18n";
import { getAppLanguage } from "../../../utils/appLanguage";
import { useNavigate } from "react-router-dom";
import { api } from "../../../services/api";
import { useChat, Message, AstriaIndiaV2Data } from "../../../contexts/ChatContext";
import { useCategories } from "../../../contexts/CategoryContext";
import { useSubCategories } from "../../../contexts/SubCategoryContext";
import { useAuth } from "../../../contexts/AuthContext";
import { getIndiaV2Language } from "../../../utils/India/astriaIndiaV2Language";

// ─── Simple, linear flow for the Astria India v2 wizard tabs:
//
//   Fill form → Click Generate → API call → receive response → show it
//
// Deliberately does NOT go through ChatContext's chats/currentChat list —
// that machinery exists for the multi-turn /sanctuary chat UI (persisted
// chat threads, history replay, cross-component sync via refs) and none of
// it applies here: each wizard submission is a single, self-contained
// question-and-answer with no history and no other component needs to see
// it. Routing through that shared state was the source of every previous
// bug in this hook (state-flush races, stale messages masking new ones,
// synthetic error messages misread as content) — all of them were about
// reading someone else's list back out after the fact. Calling the
// streaming endpoint directly and reading its own response removes the
// "after the fact" step entirely: the data used to render the result is
// the exact data this call received, nothing else.
//
// "Continue in Chat" (goToChat) is the one place this tab intentionally
// hands off into that shared multi-turn world — starting a normal
// /sanctuary chat is exactly what ChatContext.startNewChat is for.

interface UseAstriaIndiaV2TabOptions {
  categoryName: string;
  subCategoryName: string;
}

interface UseAstriaIndiaV2TabReturn {
  loading: boolean;
  error: string | null;
  message: Message | null;
  userDob: string | null;
  send: (text: string) => Promise<void>;
  reset: () => void;
  goToChat: () => Promise<void>;
}

export function useAstriaIndiaV2Tab({
  categoryName,
  subCategoryName,
}: UseAstriaIndiaV2TabOptions): UseAstriaIndiaV2TabReturn {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { startNewChat, setCurrentChat } = useChat();
  const { categories } = useCategories();
  const { subCategories } = useSubCategories();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<Message | null>(null);

  const send = async (text: string) => {
    setLoading(true);
    setError(null);
    setMessage(null);

    const category = categories.find((c) => c.name === categoryName);
    const categoryId = category?.id || null;

    if (!categoryId) {
      setError(
        i18n.t("tabErr.notSetup"),
      );
      setLoading(false);
      return;
    }

    const subCategory = subCategories.find(
      (sc) => sc.categoryId === categoryId && sc.name === subCategoryName,
    );
    const subCategoryId = subCategory?.id || null;

    if (!subCategoryId) {
      setError(
        i18n.t("tabErr.notSetup"),
      );
      setLoading(false);
      return;
    }

    try {
      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("token");
      const baseUrl = api.defaults.baseURL || "";

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(
        `${baseUrl}/backend/chat/create?stream=true`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            userMessage: text,
            categoryId,
            subCategoryId,
            userId: userId || undefined,
            language: getIndiaV2Language(),
          }),
        },
      );

      if (!response.ok || !response.body) {
        let errMsg = i18n.t("tabErr.server");
        try {
          const errData = await response.json();
          if (errData?.message && getAppLanguage() === "en") errMsg = errData.message;
        } catch {
          // ignore — keep default message
        }
        setError(errMsg);
        setLoading(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let content = "";
      let astriaIndiaV2Data: AstriaIndiaV2Data | null = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          for (const line of part.split("\n")) {
            if (!line.startsWith("data:")) continue;
            const dataStr = line.replace(/^data:\s*/, "");
            if (!dataStr) continue;

            let payload: any;
            try {
              payload = JSON.parse(dataStr);
            } catch {
              continue;
            }

            if (payload?.text) {
              content += payload.text;
            }
            if (payload?.done && payload?.astriaIndiaV2Data) {
              astriaIndiaV2Data = payload.astriaIndiaV2Data;
            }
          }
        }
      }

      if (!content.trim()) {
        setError(
          i18n.t("tabErr.noReading"),
        );
        setLoading(false);
        return;
      }

      setMessage({
        id: `ai_${Date.now()}`,
        content,
        sender: "ai",
        timestamp: new Date(),
        categoryId,
        subCategoryId,
        astriaIndiaV2Data: astriaIndiaV2Data || undefined,
      });
    } catch (err) {
      console.error(`Astria India v2 (${subCategoryName}) error:`, err);
      setError(i18n.t("tabErr.server"));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMessage(null);
    setError(null);
  };

  // "Continue in Chat" opens the app-wide "Companion Talk" category itself
  // (no subcategory) — same destination as Sanctuary's floating Astria Talk
  // Talk button and every other V2 lane — not a fresh chat on the India V2
  // subcategory the user just used.
  const goToChat = async () => {
    try {
      const category = categories.find((c) => c.name === "Companion Talk");
      const newChat = await startNewChat(category?.id || undefined);
      setCurrentChat(newChat);
      navigate(`/sanctuary?chat=${newChat.id}`);
    } catch (err) {
      console.error("Failed to start Companion Talk chat:", err);
      setError(i18n.t("tabErr.startChat"));
    }
  };

  return {
    loading,
    error,
    message,
    userDob: user?.dob || null,
    send,
    reset,
    goToChat,
  };
}
